/**
 * Getypeerde toegang tot `/residences/:id/spaces`: de kamers en plekken van een
 * woning, en waar een foto bij hoort.
 *
 * De naam van een ruimte komt uit twee bronnen en dat is opzet. De soort is een
 * vaste woordenlijst en wordt vertaald (§17.8); de naam die de verhuurder zelf
 * geeft, is data en gaat ongewijzigd op het scherm. `spaceLabel()` hieronder is de
 * enige plek waar die twee worden samengevoegd — verspreid over de componenten
 * levert dat drie verschillende schrijfwijzen van "Badkamer 2" op.
 */
import { call, isRecord, readCount, readText } from './http'
import type { Failure, Result } from './http'
import type { RequestOptions } from './api-client'

export type SpaceFailure = Failure
export type SpaceResult<T> = Result<T>

/** De soorten uit migratie 0030, in de volgorde waarin een woning wordt doorlopen. */
export const SPACE_KINDS = [
  'living',
  'dining',
  'kitchen',
  'bedroom',
  'bathroom',
  'toilet',
  'office',
  'hallway',
  'utility',
  'storage',
  'garden',
  'terrace',
  'balcony',
  'pool',
  'wellness',
  'parking',
  'exterior',
  'other',
] as const
export type SpaceKind = (typeof SPACE_KINDS)[number]

export interface Space {
  readonly id: string
  readonly residenceId: string
  readonly kind: SpaceKind
  readonly ordinal: number
  readonly label: string
  readonly sortOrder: number
  readonly photoCount: number
}

function isKind(value: unknown): value is SpaceKind {
  const known: readonly string[] = SPACE_KINDS
  return typeof value === 'string' && known.includes(value)
}

function readSpace(value: unknown): Space | null {
  if (!isRecord(value)) {
    return null
  }
  const id = readText(value, 'id')
  const kind = value['kind']
  // De soort is dragend: hij bepaalt de naam, de sortering en straks wat een kanaal
  // ervan maakt. Een ruimte met een onbekende soort wordt overgeslagen in plaats van
  // als "overig" getoond.
  if (id === '' || !isKind(kind)) {
    return null
  }
  return {
    id,
    kind,
    residenceId: readText(value, 'residenceId'),
    ordinal: readCount(value, 'ordinal'),
    label: readText(value, 'label'),
    sortOrder: readCount(value, 'sortOrder'),
    photoCount: readCount(value, 'photoCount'),
  }
}

function readList(payload: unknown): Space[] | null {
  if (!isRecord(payload) || !Array.isArray(payload['spaces'])) {
    return null
  }
  const spaces: Space[] = []
  for (const entry of payload['spaces']) {
    const space = readSpace(entry)
    if (space !== null) {
      spaces.push(space)
    }
  }
  return spaces
}

export async function listSpaces(
  residenceId: string,
  options: RequestOptions = {},
): Promise<SpaceResult<readonly Space[]>> {
  return call(
    'GET',
    `/residences/${encodeURIComponent(residenceId)}/spaces`,
    undefined,
    readList,
    options,
  )
}

export async function createSpace(
  residenceId: string,
  body: { readonly kind: SpaceKind; readonly label: string },
): Promise<SpaceResult<Space>> {
  return call('POST', `/residences/${encodeURIComponent(residenceId)}/spaces`, body, (payload) =>
    isRecord(payload) ? readSpace(payload['space']) : null,
  )
}

export async function updateSpace(
  residenceId: string,
  spaceId: string,
  body: Partial<{ kind: SpaceKind; label: string; ordinal: number; sortOrder: number }>,
): Promise<SpaceResult<Space>> {
  return call(
    'PATCH',
    `/residences/${encodeURIComponent(residenceId)}/spaces/${encodeURIComponent(spaceId)}`,
    body,
    (payload) => (isRecord(payload) ? readSpace(payload['space']) : null),
  )
}

export async function deleteSpace(
  residenceId: string,
  spaceId: string,
): Promise<SpaceResult<{ unassignedPhotos: number }>> {
  return call(
    'DELETE',
    `/residences/${encodeURIComponent(residenceId)}/spaces/${encodeURIComponent(spaceId)}`,
    undefined,
    (payload) =>
      isRecord(payload) ? { unassignedPhotos: readCount(payload, 'unassignedPhotos') } : null,
  )
}

/** Hangt een foto aan een ruimte; `null` haalt hem eruit. */
export async function assignSpace(
  residenceId: string,
  assetId: string,
  spaceId: string | null,
): Promise<SpaceResult<true>> {
  return call(
    'PUT',
    `/residences/${encodeURIComponent(residenceId)}/media/${encodeURIComponent(assetId)}/space`,
    { spaceId },
    () => true,
  )
}

/**
 * De naam van een ruimte zoals hij op het scherm hoort.
 *
 * De eigen naam wint. Is die er niet, dan de vertaalde soort — met het volgnummer
 * erachter, maar alleen wanneer er méér dan één van die soort is. "Badkamer 1"
 * terwijl er maar één badkamer is, leest als een fout.
 */
export function spaceLabel(
  space: Space,
  all: readonly Space[],
  translate: (key: string) => string,
): string {
  if (space.label !== '') {
    return space.label
  }
  const kindName = translate(`dashboard.spaces.kind.${space.kind}`)
  const sameKind = all.filter((other) => other.kind === space.kind).length
  return sameKind > 1 ? `${kindName} ${String(space.ordinal)}` : kindName
}

/** Vertaalsleutel bij een mislukte aanroep. */
export function spaceErrorMessageKey(failure: SpaceFailure): string {
  switch (failure.code) {
    case 'validation_failed':
      return 'dashboard.spaces.error.rejected'
    case 'not_found':
      return 'dashboard.spaces.error.notFound'
    case 'forbidden':
      return 'dashboard.spaces.error.forbidden'
    case 'conflict':
      return 'dashboard.spaces.error.conflict'
    default:
      return 'dashboard.spaces.error.unavailable'
  }
}
